/**
 * Builds the error report payload — the contract the SDK sends to the backend
 * (POST /api/ingestion/errors). This is intentionally the single place that
 * defines the shape, so the unit test for it doubles as the living spec that
 * the backend repo can build against.
 *
 * Pure and dependency-free; covered by __tests__/error-payload.test.ts.
 */

import { parseStack, StackFrame } from './stack-parser';
import { Breadcrumb } from './breadcrumbs';
import { collectLinkedErrors, LinkedException } from './linked-errors';
import { getDebugIdMapForFrames } from './debug-ids';

/**
 * Random 32-hex idempotency ID (kept dependency-free to match this module).
 * Falls back to Math.random outside secure contexts; uniqueness (not
 * unpredictability) is all dedup needs.
 */
function mintEventId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    }
    let out = '';
    for (let i = 0; i < 32; i += 1) out += Math.floor(Math.random() * 16).toString(16);
    return out;
}

export type ErrorMechanism =
    | 'onerror'
    | 'onunhandledrejection'
    | 'resource'
    | 'csp'
    | 'react'
    | 'console'
    | 'captureException';

/** Connectivity at the moment the error fired — helps reproduce offline/flaky bugs. */
export interface NetworkState {
    online: boolean;
    effectiveType: string | null;
}

/**
 * The most recent error-correlated network request (a failed fetch/XHR), with
 * bodies/headers redacted. Bodies/headers are only present when the host opted
 * in via `captureRequestBodies`.
 */
export interface RequestContext {
    url: string;
    method: string;
    status: number | null;
    errorType?: string;
    durationMs?: number;
    requestHeaders?: Record<string, string>;
    requestBody?: string;
    responseBody?: string;
}

export interface ErrorReportInput {
    /** The thrown value: an Error, a string, or anything else. */
    error: unknown;
    mechanism: ErrorMechanism;
    handled: boolean;
    sessionId: string;
    endUserId: string | null;
    url: string;
    breadcrumbs: Breadcrumb[];
    release?: string | null;
    environment?: string | null;
    /** Git commit SHA the build was cut from — turns a culprit frame into a GitHub blob link. */
    commitSha?: string | null;
    /** Build/dist discriminator (e.g. build id) — pairs with `release` to locate the right source map. */
    dist?: string | null;
    /** React component stack (set by the error boundary). */
    componentStack?: string;
    /** Connectivity snapshot at error time. */
    networkState?: NetworkState;
    /** Last error-correlated network request (redacted). */
    requestContext?: RequestContext;
    /** Epoch ms the replay session started — lets the backend compute replay offset. */
    sessionStartTimestampMs?: number;
    automaticProperties?: Record<string, unknown>;
    userProperties?: Record<string, unknown>;
    sessionProperties?: Record<string, unknown>;
    timestampMs?: number;
}

export interface ErrorReport {
    /**
     * Client-minted idempotency ID. Set once at capture time; retries reuse
     * the same serialized body, so the server can drop duplicate deliveries
     * without touching occurrence counts.
     */
    eventId: string;
    exceptionType: string;
    value: string;
    stackFrames: StackFrame[];
    mechanism: ErrorMechanism;
    handled: boolean;
    release: string | null;
    environment: string | null;
    commitSha: string | null;
    dist: string | null;
    componentStack?: string;
    networkState?: NetworkState;
    requestContext?: RequestContext;
    sessionStartTimestampMs?: number;
    breadcrumbs: Breadcrumb[];
    sessionId: string;
    endUserId: string | null;
    url: string;
    timestampMs: number;
    automaticProperties?: Record<string, unknown>;
    userProperties?: Record<string, unknown>;
    sessionProperties?: Record<string, unknown>;
    /** Cause chain (`Error.cause` / `AggregateError.errors`), outermost first. */
    linkedErrors?: LinkedException[];
    /** filename → debug ID for the frames' files (from bundler-injected globals). */
    debugIdMap?: Record<string, string>;
}

/**
 * Normalize an arbitrary thrown value into a type/message/stack triple.
 */
export function describeError(error: unknown): { type: string; value: string; stack: string | null } {
    if (error instanceof Error) {
        return {
            type: error.name || 'Error',
            value: error.message || '',
            stack: error.stack || null,
        };
    }
    if (typeof error === 'string') {
        return { type: 'Error', value: error, stack: null };
    }
    if (error == null) {
        return { type: 'Error', value: String(error), stack: null };
    }
    // Some libraries reject with a plain object that carries name/message.
    if (typeof error === 'object') {
        const obj = error as Record<string, unknown>;
        const type = typeof obj.name === 'string' && obj.name ? obj.name : 'Error';
        let value: string;
        if (typeof obj.message === 'string') {
            value = obj.message;
        } else {
            try {
                value = JSON.stringify(error);
            } catch {
                value = String(error);
            }
        }
        const stack = typeof obj.stack === 'string' ? obj.stack : null;
        return { type, value, stack };
    }
    return { type: 'Error', value: String(error), stack: null };
}

export function buildErrorReport(input: ErrorReportInput): ErrorReport {
    const described = describeError(input.error);
    const stackFrames = parseStack(described.stack);
    const linkedErrors = collectLinkedErrors(input.error);
    const linkedFrames: StackFrame[] = [];
    for (const linked of linkedErrors) {
        linkedFrames.push(...linked.stackFrames);
    }
    const debugIdMap = getDebugIdMapForFrames(
        linkedFrames.length ? stackFrames.concat(linkedFrames) : stackFrames,
    );
    return {
        eventId: mintEventId(),
        exceptionType: described.type,
        value: described.value,
        stackFrames,
        mechanism: input.mechanism,
        handled: input.handled,
        release: input.release ?? null,
        environment: input.environment ?? null,
        commitSha: input.commitSha ?? null,
        dist: input.dist ?? null,
        componentStack: input.componentStack,
        networkState: input.networkState,
        requestContext: input.requestContext,
        sessionStartTimestampMs: input.sessionStartTimestampMs,
        breadcrumbs: input.breadcrumbs,
        sessionId: input.sessionId,
        endUserId: input.endUserId,
        url: input.url,
        timestampMs: input.timestampMs ?? Date.now(),
        automaticProperties: input.automaticProperties,
        userProperties: input.userProperties,
        sessionProperties: input.sessionProperties,
        linkedErrors: linkedErrors.length ? linkedErrors : undefined,
        debugIdMap,
    };
}

/**
 * Key used for client-side de-dup. Built from the error type, message, and
 * every stack frame's location+function (Sentry's dedupe compares the full
 * stack), so distinct code paths that share a top frame are NOT collapsed
 * while the identical crash repeated in a burst is.
 */
export function dedupeKey(report: ErrorReport): string {
    const frames = report.stackFrames.length
        ? report.stackFrames
              .map((f) => `${f.function ?? '?'}@${f.file ?? '?'}:${f.line ?? '?'}:${f.column ?? '?'}`)
              .join('|')
        : 'no-stack';
    return `${report.exceptionType}|${report.value}|${frames}`;
}
